import React, { Fragment, ReactElement, useEffect, useState } from 'react';

import API from '@sesame/web-api';
import { ILoadState, IRack, IRackComponent, IComponent, ISalesorder, IPortMap } from 'src/types';
import Alert, { AlertTypes } from 'src/components/AlertDisplay';
import { EditComponentPortmap } from './EditComponentPortmap';
import { useAuth0 } from '@auth0/auth0-react';
import { Spinner } from 'src/components/Spinner';


interface IRackComponentsProps {
  componentTree: IRackComponent;
  onElevationChange: (componentTree?: IRackComponent) => void;
  elevationUpdateKey: number;
}
export const RackOUComponents = ({ componentTree, onElevationChange, elevationUpdateKey }: IRackComponentsProps) => {
  const  {isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);

  const getTreeComponent = (componentId: number, part?: IRackComponent): IRackComponent | undefined => {
    if (!componentTree) {
      return undefined;
    }
    const comp = part || componentTree;
    if (comp.componentId == componentId) {
      return comp;
    }
    for (let c of comp.components) {
      const sub = getTreeComponent(componentId, c);
      if (sub) {
        return sub;
      }
    }
    return undefined;
  }

  const countMatchingPorts = (component: IRackComponent, sw: string, bpt: number, lpt?: number) => {
    let pcount = 0;
    for (let c of component.components) {
      pcount += countMatchingPorts(c, sw, bpt, lpt);
    }

    const bezelPort = new RegExp(`^${sw}:P?${bpt}`);
    const bezelOnly = new RegExp(`^${sw}:P?${bpt}$`);
    const logicalPort = new RegExp(`^${sw}:P?${bpt}:S?${lpt}`);
    for (let p of component.portmap) {
      if (bezelPort.test(p)) {
        if (lpt) {
          if (logicalPort.test(p) || bezelOnly.test(p)) {
            pcount++;
          }
        } else {
          pcount++;
        }
      }
    }
    return pcount;
  }

  const checkPortDestinations = (componentId: number, portMap: string[]) => {

    const errors: string[] = [];
    portMap.forEach(p => {
      if (!p) {
        errors.push('');
      } else {
        const [all, sw, bpt, grp1, lpt] = p.match(/^(.*):P?(\d+)(:S?([1-4]))?$/) || [undefined, undefined, undefined, undefined, undefined];
        if (!sw || !bpt) {
          errors.push('InvalidReference');
        } else {
          const targetSwitch = componentTree.components.find(c => c.type == 'Switches' && c.label == sw);
          if (!targetSwitch) {
            errors.push('InvalidSwitch');
          } else {
            if (+bpt > targetSwitch.portCount) {
              errors.push('InvalidPort');
            } else {
              const lpt_num = lpt ? +lpt : undefined;
              const dupes = countMatchingPorts(componentTree, sw.replace(".", "\."), +bpt, lpt_num);
              if (dupes > 1) {
                errors.push('TooManyPorts');
              } else {
                errors.push('Valid');
              }
            }
          }
        }
      }
    });
    return errors;
  }

  function onSaveOU(componentId: number, location: IInputLocation) {
    const comp = getTreeComponent(componentId)
    if (comp == undefined) {
      console.log('error finding tree element ' + componentId);
      return;
    }
    console.log('onSaveOU: ' + JSON.stringify(location));
    comp.location = {
      ou: +location.ou,
      column: +location.column,
      height: +location.height,
    }
    forceUpdate();
  }

  const saveRack = (e: React.FormEvent<HTMLButtonElement>) => {
    e.preventDefault();

    (async function() {
      const accessToken = await getAccessTokenSilently();
      try {

        for await (const c of componentTree.components.filter(
          c => (c.location && c.location.ou && c.location.column && c.location.height) || c.type == 'Switches')) {
          console.log('posting ' + JSON.stringify(c.location));
          API.post(accessToken, `/api/v1/component/${c.componentId}/location`, c.location!)
          if (c.type == 'Switches') {
            // update the label
            API.patch(accessToken, `/api/v1/component`, c);
          }
        }
      } catch (e: any) {
        Alert(AlertTypes.ERROR, e.message ?? "Unkown error: " + JSON.stringify(e));
        return;
      }
      onElevationChange();
    })();

  }

  if (isLoading) {
    return <Spinner />
  }
  return <form>
    <div data-uk-grid>
      <div className="uk-width-1-4">
        <button className="uk-button uk-button-primary"
          onClick={(e) => saveRack(e)}
        >Save
        </button>
      </div>
      <div className="uk-width-3-4 uk-card uk-card-default">
        <p className="uk-padding-small">Enter Elevation for each component and labels for each switch.
        Cabling should be entered using this format: <pre>switch_label:port:subport</pre>
          e.g. <span className="uk-text-secondary">SWA:P1:S1</span>.
          Enter all cabling connetions from the downlink side <b>ONLY</b>.
          For in-rack connections enter switch targets on each server NIC card.
          Enter uplink connections in switches. Note that
          uplinks will fail validation.  The uplink will be saved.
        </p>
      </div>
    </div>
    <div className="uk-width-1-1">
      <table className="uk-table uk-table-small">
        <thead>
          <tr>
            <th className="uk-text-nowrap">Part:Rev</th>
            <th className="uk-text-nowrap">OU | Col | Ht</th>
            <th>Name</th>
          </tr>
        </thead>
        <tbody>
          <DisplayRackChildren
            part={componentTree}
            onSave={onSaveOU}
            onElevationChange={onElevationChange}
            checkPortDestinations={checkPortDestinations}
            elevationUpdateKey={elevationUpdateKey} />
        </tbody>
      </table>
    </div>
  </form >
}

interface DisplayRackChildrenProps {
  part: IRackComponent;
  onElevationChange: () => void;
  onSave: (componentId: number, location: IInputLocation) => void;
  checkPortDestinations: (componentId: number, portMap: string[]) => string[];
  elevationUpdateKey: number;
}
interface IInputLocation {
  ou: string,
  column: string;
  height: string;
}
const DisplayRackChildren = ({ part,
  onSave,
  onElevationChange,
  checkPortDestinations,
  elevationUpdateKey }: DisplayRackChildrenProps) => {

  const [label, setLabel] = useState(part.label || '');
  const [location, setLocation] = useState<IInputLocation>(part.location ? {
    ou: part.location.ou ? `${part.location.ou}` : '',
    column: part.location.column ? `${part.location.column}` : '',
    height: part.location.height ? `${part.location.height}` : '2',
  } : { ou: '', column: '', height: '2' });
  const [expand, setExpand] = useState(part.level < 2);

  const saveOnChange = (loc: IInputLocation) => {
    if (loc.ou && loc.column && loc.height) {
      onSave(part.componentId, loc);
    }
  }

  const onOUChange = (e: React.FormEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value.toUpperCase();
    const loc = { ...location, ou: val };
    setLocation(loc);
    if (/^\d+/.test(val)) {
      saveOnChange(loc);
    }
  }
  const onColChange = (e: React.FormEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value;
    const loc = { ...location, column: val };
    setLocation(loc);
    if (/^\d+/.test(val)) {
      saveOnChange(loc);
    }
  }
  const onHeightChange = (e: React.FormEvent<HTMLInputElement>) => {
    const val = e.currentTarget.value;
    const loc = { ...location, height: val };
    setLocation(loc);
    if (/^\d+/.test(val)) {
      saveOnChange(loc);
    }
  }
  const onLabelChange = (e: React.FormEvent<HTMLInputElement>) => {
    setLabel(e.currentTarget.value);
    part.label = e.currentTarget.value;
  }

  return <>
    <tr>
      <td className="uk-preserve-width uk-text-nowrap">
        {[...Array(part.level! - 1)].map((e, i) => <span key={`${i}_${part.componentId}`} data-uk-icon="chevron-double-right" />)}
        {!expand && part.components.length > 0 ? <span data-uk-icon="triangle-right" onClick={(e) => setExpand(true)} /> : <></>}
        {expand && part.components.length > 0 ? <span data-uk-icon="triangle-down" onClick={(_e) => setExpand(false)} /> : <></>}
        {part.components.length == 0 ? <span data-uk-icon="blank" /> : <></>}
        {part.partNumber}:{part.revision}
      </td>
      <td className="uk-text-nowrap">
        {part.level == 2 ? <>
          <input type="text"
            size={1}
            maxLength={4}
            onChange={e => onOUChange(e)}
            value={location.ou}
            className="uk-input uk-form-small uk-form-width-xsmall"
          />
          <input type="text"
            size={1}
            maxLength={4}
            onChange={e => onColChange(e)}
            value={location.column}
            className="uk-input uk-form-small uk-form-width-xsmall"
          />
          <input type="text"
            size={1}
            maxLength={1}
            onChange={e => onHeightChange(e)}
            value={location.height}
            className="uk-input uk-form-small uk-form-width-xsmall"
          />
        </> : <></>
        }
      </td>
      <td>
        {part.type == 'Switches' ? <input type="text"
          size={3}
          maxLength={20}
          onChange={e => onLabelChange(e)}
          value={label}
          placeholder="Label"
          className="uk-input uk-form-small uk-form-width-small uk-margin-right"
        /> : <></>}
        {part.name}
        {part.portCount ?
          <EditComponentPortmap
            component={part}
            onElevationChange={onElevationChange}
            checkPortDestinations={checkPortDestinations}
            elevationUpdateKey={elevationUpdateKey}
          />
          : <></>
        }
      </td>
    </tr>
    {expand ? <>{part.components.map(p => <DisplayRackChildren
      part={p}
      onSave={onSave}
      onElevationChange={onElevationChange}
      checkPortDestinations={checkPortDestinations}
      elevationUpdateKey={elevationUpdateKey}
      key={p.componentId} />)}</> : <></>}
  </>


}
